// Frontend Logger - sends logs to backend for file storage

type LogLevel = 'info' | 'warn' | 'error' | 'debug';

interface LogData {
  [key: string]: any;
}

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  data: LogData;
}

class FrontendLogger {
  private logQueue: LogEntry[];
  private isProcessing: boolean;
  private batchSize: number;
  private maxRetries: number;
  private apiBaseUrl: string | undefined;

  constructor() {
    this.logQueue = [];
    this.isProcessing = false;
    this.batchSize = 10;
    this.maxRetries = 3;
    this.apiBaseUrl = import.meta.env.VITE_API_URL;

    // Process queue every 5 seconds
    setInterval(() => this.processQueue(), 5000);

    // Process queue on page unload
    window.addEventListener('beforeunload', () => this.flush());
  }

  log(level: LogLevel, message: string, data: LogData = {}): void {
    const logEntry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      data: {
        ...data,
        url: window.location.href,
        userAgent: navigator.userAgent,
        sessionId: this.getSessionId(),
      },
    };

    // Also log to console for immediate debugging
    const consoleMethod = (console as any)[level] || console.log;
    consoleMethod(`[${logEntry.timestamp}] ${message}`, data);

    this.logQueue.push(logEntry);

    // Process immediately for errors
    if (level === 'error') {
      this.processQueue();
    }
  }

  info(message: string, data: LogData = {}): void {
    this.log('info', message, data);
  }

  warn(message: string, data: LogData = {}): void {
    this.log('warn', message, data);
  }

  error(message: string, data: LogData = {}): void {
    this.log('error', message, data);
  }

  debug(message: string, data: LogData = {}): void {
    this.log('debug', message, data);
  }

  // Log API requests
  logApiRequest(method: string, url: string, requestData: any = {}): void {
    this.info('Frontend API Request', {
      type: 'api_request',
      method,
      url,
      requestSize: JSON.stringify(requestData).length,
    });
  }

  // Log API responses
  logApiResponse(method: string, url: string, status: number, responseData: any = {}, duration: number = 0): void {
    const level: LogLevel = status >= 400 ? 'error' : 'info';
    this[level]('Frontend API Response', {
      type: 'api_response',
      method,
      url,
      status,
      responseSize: JSON.stringify(responseData).length,
      duration: `${duration}ms`,
    });
  }

  // Log React errors
  logReactError(error: Error, errorInfo: any): void {
    this.error('React Error', {
      type: 'react_error',
      error: {
        message: error.message,
        stack: error.stack,
      },
      errorInfo,
    });
  }

  // Log user actions
  logUserAction(action: string, details: LogData = {}): void {
    this.info('User Action', {
      type: 'user_action',
      action,
      ...details,
    });
  }

  // Log page navigation
  logPageNavigation(from: string, to: string): void {
    this.info('Page Navigation', {
      type: 'navigation',
      from,
      to,
    });
  }

  async processQueue(): Promise<void> {
    if (this.isProcessing || this.logQueue.length === 0) {
      return;
    }

    this.isProcessing = true;
    let batch: LogEntry[] = [];

    try {
      batch = this.logQueue.splice(0, this.batchSize);
      await this.sendLogsToBackend(batch);
    } catch (error) {
      console.error('Failed to send logs to backend:', error);
      // Put failed logs back at the beginning of the queue
      if (batch.length > 0) {
        this.logQueue.unshift(...batch);
      }
    } finally {
      this.isProcessing = false;
    }
  }

  async sendLogsToBackend(logs: LogEntry[], retryCount: number = 0): Promise<void> {
    try {
      const response = await fetch(`${this.apiBaseUrl}/api/logs/frontend`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ logs }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      if (retryCount < this.maxRetries) {
        // Exponential backoff
        const delay = 2 ** retryCount * 1000;
        await new Promise<void>((resolve) => setTimeout(resolve, delay));
        return this.sendLogsToBackend(logs, retryCount + 1);
      } else {
        throw error;
      }
    }
  }

  flush(): void {
    if (this.logQueue.length > 0) {
      // Use sendBeacon for reliable log delivery on page unload
      const logs = this.logQueue.splice(0);
      if (navigator.sendBeacon) {
        navigator.sendBeacon(`${this.apiBaseUrl}/api/logs/frontend`, JSON.stringify({ logs }));
      }
    }
  }

  getSessionId(): string {
    let sessionId = sessionStorage.getItem('logSessionId');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('logSessionId', sessionId);
    }
    return sessionId;
  }
}

// Create global logger instance
const logger: FrontendLogger = new FrontendLogger();

// Global error handler
window.addEventListener('error', (event: ErrorEvent) => {
  logger.error('Global JavaScript Error', {
    type: 'global_error',
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    stack: event.error?.stack,
  });
});

// Unhandled promise rejection handler
window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
  logger.error('Unhandled Promise Rejection', {
    type: 'unhandled_rejection',
    reason: event.reason?.message || event.reason,
    stack: event.reason?.stack,
  });
});

export { logger };
